import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Container, Box, CircularProgress, Paper, Typography, Button } from '@mui/material';
import PersonalTransitCalendar from '../components/PersonalTransitCalendar';
import AdminUserPanel from '../components/AdminUserPanel';
import TransitCalendarLegend from '../components/TransitCalendarLegend';
import { getNatalData } from '../services/transitCalendarService';
import { resolveBirthMoment } from '../utils/natalDate';
import { getCalendarAccess } from '../utils/calendarAccess';

const TransitCalendarPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isAdminMode = searchParams.get('admin') === 'true';
  const profileId = searchParams.get('profile_id');

  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [natalData, setNatalData] = useState(null);
  const [profileData, setProfileData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadProfileData = async () => {
      // Check authentication
      const token = localStorage.getItem('transit-calendar-token');
      if (!token) {
        navigate('/transit-calendar/login');
        return;
      }

      // If no profile_id, redirect to profile selection
      if (!profileId) {
        navigate('/transit-calendar/profiles');
        return;
      }

      // Fetch specific profile data from API
      try {
        const data = await getNatalData(profileId);
        setProfileData(data);
        setNatalData(data);  // Keep for backwards compatibility
        setIsAuthenticated(true);
      } catch (err) {
        console.error('Error loading profile data from API:', err);
        // If fetch fails, redirect to profile selection
        navigate('/transit-calendar/profiles');
      }

      setLoading(false);
    };

    loadProfileData();
  }, [navigate, profileId]);

  const handleLogout = () => {
    localStorage.removeItem('transit-calendar-token');
    localStorage.removeItem('transit-calendar-access-token');
    localStorage.removeItem('transit-calendar-refresh-token');
    localStorage.removeItem('transit-calendar-user');
    localStorage.removeItem('transit-calendar-email');
    navigate('/transit-calendar/login');
  };

  if (loading) {
    return (
      <Container maxWidth="xl" sx={{ py: 8, textAlign: 'center' }}>
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ mt: 2, color: 'text.secondary' }}>
          Loading your Cosmic Personal Events Calendar...
        </Typography>
      </Container>
    );
  }

  if (!isAuthenticated || !natalData) {
    return null; // Will redirect via useEffect
  }

  // Birth moment resolved device-independently (fixes natal chart shifting
  // when the site is opened from a different timezone than registration)
  const birthMoment = resolveBirthMoment(natalData);
  const natalBirthDate = birthMoment ? birthMoment.localDate : new Date(natalData.birth_date);

  // Permissions from DynamoDB: weekly is free, monthly and annual are paid (annual calendars are sold per year)
  const tthAccess = getCalendarAccess(natalData.permissions, 'tth');
  const hasPaidView = tthAccess.monthly || tthAccess.annual;

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header with user info and logout */}
      <Box sx={{
        mb: 3,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: { xs: 'flex-start', sm: 'center' },
        flexDirection: { xs: 'column', sm: 'row' },
        gap: 2
      }}>
        <Box sx={{ flex: 1 }}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' }
            }}
          >
            {natalData.profile_name || natalData.name}'s Cosmic Personal Events Calendar
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
          >
            {natalData.birth_location} • {natalBirthDate.toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit'
            })}
          </Typography>
        </Box>
        <Box sx={{
          display: 'flex',
          gap: 1,
          width: { xs: '100%', sm: 'auto' },
          flexDirection: { xs: 'row', sm: 'row' }
        }}>
          <Button
            variant="outlined"
            onClick={() => navigate('/transit-calendar/profiles')}
            sx={{
              borderColor: '#667eea',
              color: '#667eea',
              flex: { xs: 1, sm: 'none' },
              fontSize: { xs: '0.8rem', sm: '0.875rem' },
              px: { xs: 1, sm: 2 },
              '&:hover': { borderColor: '#764ba2', bgcolor: 'rgba(102, 126, 234, 0.04)' }
            }}
          >
            All Profiles
          </Button>
          <Button
            variant="outlined"
            onClick={handleLogout}
            sx={{
              borderColor: '#667eea',
              color: '#667eea',
              flex: { xs: 1, sm: 'none' },
              fontSize: { xs: '0.8rem', sm: '0.875rem' },
              px: { xs: 1, sm: 2 },
              '&:hover': { borderColor: '#764ba2', bgcolor: 'rgba(102, 126, 234, 0.04)' }
            }}
          >
            Log Out
          </Button>
        </Box>
      </Box>

      {/* Admin Panel */}
      {isAdminMode && <AdminUserPanel />}

      {/* Legend/Guide */}
      <TransitCalendarLegend calendarType="cosmic-personal-events" />

      {/* Free Tier Notice - Only show for free users */}
      {!hasPaidView && (
        <Paper sx={{
          p: { xs: 2, sm: 3 },
          mb: 3,
          background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)',
          border: '2px solid rgba(102, 126, 234, 0.3)'
        }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              mb: 1,
              fontSize: { xs: '1rem', sm: '1.25rem' }
            }}
          >
            🆓 Free Weekly View
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              mb: 2,
              fontSize: { xs: '0.8rem', sm: '0.875rem' }
            }}
          >
            You're viewing the current week's transits. Upgrade to unlock monthly/annual views and week navigation.
          </Typography>
          <Button
            variant="contained"
            size="small"
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              fontSize: { xs: '0.75rem', sm: '0.8125rem' },
              py: { xs: 0.75, sm: 1 },
              px: { xs: 1.5, sm: 2 },
              '&:hover': { background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)' }
            }}
            onClick={() => navigate('/pricing?section=calendar')}
          >
            Upgrade for Full Access
          </Button>
        </Paper>
      )}

      {/* Personal Transit Calendar */}
      {(() => {
        const permissions = natalData.permissions || {};
        const tthWeekly = tthAccess.weekly;
        const tthMonthly = tthAccess.monthly;
        const tthAnnual = tthAccess.annual;

        // Keep old logic for backward compatibility
        const showNav = tthMonthly || tthAnnual;
        const allowedYears = [];
        if (permissions.year_2026?.tth_monthly || permissions.year_2026?.tth_annual) {
          allowedYears.push(2026);
        }
        if (permissions.year_2027?.tth_monthly || permissions.year_2027?.tth_annual) {
          allowedYears.push(2027);
        }
        if (permissions.year_2028?.tth_monthly || permissions.year_2028?.tth_annual) {
          allowedYears.push(2028);
        }
        const monthlyAccess = tthMonthly && !tthAnnual;

        return (
          <PersonalTransitCalendar
            natalDate={natalBirthDate}
            natalLatitude={natalData.birth_lat}
            natalLongitude={natalData.birth_lon}
            natalTimezone={natalData.timezone}
            firstHouseReference="ascendant"
            cityName={natalData.birth_location}
            showWeekNavigation={showNav}
            currentWeekOnly={!showNav}
            allowedYears={allowedYears}
            monthlyAccess={monthlyAccess}
            tthWeekly={tthWeekly}
            tthMonthly={tthMonthly}
            tthAnnual={tthAnnual}
            tthWeeklyStartDate={tthAccess.weeklyStartDate}
            tthMonthlyStartDate={tthAccess.monthlyStartDate}
            tthAnnualStartDate={tthAccess.annualStartDate}
            tthAnnualEndDate={tthAccess.annualEndDate}
          />
        );
      })()}

      {/* Upgrade Prompt at Bottom - Only show for free users */}
      {!hasPaidView && (
        <Paper sx={{ p: { xs: 3, sm: 4 }, mt: 4, textAlign: 'center', bgcolor: '#1a1a2e', border: '1px solid rgba(201,168,124,0.12)' }}>
          <Typography
            variant="h5"
            gutterBottom
            sx={{
              fontWeight: 700,
              fontSize: { xs: '1.25rem', sm: '1.5rem' }
            }}
          >
            Want More?
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            paragraph
            sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
          >
            Upgrade to unlock monthly and annual calendar views, navigate through past and future weeks,
            and get access to premium features like PDF exports and email notifications.
          </Typography>
          <Button
            variant="contained"
            size="large"
            sx={{
              py: { xs: 1.25, sm: 1.5 },
              px: { xs: 3, sm: 4 },
              fontSize: { xs: '0.875rem', sm: '1rem' },
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              '&:hover': { background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)' }
            }}
            onClick={() => navigate('/pricing?section=calendar')}
          >
            View Pricing Options
          </Button>
        </Paper>
      )}
    </Container>
  );
};

export default TransitCalendarPage;
